import React, { FunctionComponent, Suspense, lazy, useEffect } from 'react';
import { GlobalProviders } from '@/contexts';
import { MetaTagsSEO, Router } from '..';
import { GlobalStyles } from '.';
import { ErrorBoundary } from './GlobalErrorBoundary';
import { recordPreloadError } from './chunkPreloadError';
import { FrostTierController } from '@/components/FrostTierController/FrostTierController';
import { scheduleThirdPartyScripts } from '@/analytics/thirdParty';

// Devtools de React Query: lazy y SOLO en dev. En prod la condición es estática y
// Rolldown ni emite el chunk; en dev no entra en el módulo de arranque.
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() => import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })))
  : null;

export const App: FunctionComponent = () => {
  useEffect(() => {
    // Terceros (GTM + Meta Pixel + Intercom): las COLAS quedan listas ya —ningún
    // evento se puede perder— pero los ~490 KB de script esperan a que el main
    // thread se calme, salvo en las rutas de conversión, donde bajan al toque.
    // Ver src/analytics/thirdParty.ts.
    scheduleThirdPartyScripts();
  }, []);

  // Stop Vite's default behavior of auto-reloading the page when a dynamic
  // import preload fails. A force-reload mid-flow would destroy user state
  // (open forms, in-progress edits). If the user actually tries to use the
  // failed code path, the render error propagates to GlobalErrorBoundary,
  // which shows a friendly "Actualizar" recovery screen for chunk errors.
  //
  // Cancelar el evento tiene una contra: el import() pasa a resolver `undefined`
  // en vez de rechazar, y los lazy con named export explotan con un TypeError que
  // no se parece a un error de chunk. Dejamos la marca para que el boundary lo
  // reconozca igual. Ver chunkPreloadError.ts.
  useEffect(() => {
    const onVitePreloadError = (event: Event) => {
      recordPreloadError((event as Event & { payload?: unknown }).payload);
      event.preventDefault();
    };
    window.addEventListener('vite:preloadError', onVitePreloadError);
    return () => {
      window.removeEventListener('vite:preloadError', onVitePreloadError);
    };
  }, []);

  return (
    <GlobalProviders>
      <MetaTagsSEO title="Ágora | Red de profesionales únicos" />
      <ErrorBoundary>
        <FrostTierController />
        <Router />
        <GlobalStyles />
        {ReactQueryDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        )}
      </ErrorBoundary>
    </GlobalProviders>
  );
};
